import { useApolloClient } from '@apollo/client'
import { useCallback, useContext, useMemo, useRef } from 'react'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../data/gql/questionnaire/queries/audiences'
import { FieldworkAudience } from '../../../data/model/audience'
import { useUpdateAudienceMemberV2 } from '../../../hooks/audience/updateAudienceMemberV2'
import { useAudienceDisplayLanguage } from '../../../hooks/audience/useAudienceDisplayLanguage'
import { CUSTOM_AUDIENCE_PRESET_NAME } from '../../../hooks/audience/usePresetAudience'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import AudienceContext, { setAudienceMembers } from '../Audience.context'
import {
  getAudienceMemberQuestion,
  getAudienceMemberQuestionId,
  getSurveyQuestionLabels,
  moveMemberInList
} from '../audienceOrder'
import { overlayQuestionText } from '../audienceTranslation'
import ReorderAudienceDialogControl, {
  ReorderAudienceRowData
} from './ReorderAudienceDialog.control'

interface Props {
  open: boolean
  onClose: () => void
}

const ReorderAudienceDialog = ({ open, onClose }: Props) => {
  const { dispatch, audienceState } = useContext(AudienceContext)
  const { updateAudienceMemberV2Position } = useUpdateAudienceMemberV2()
  const marketLanguage = useAudienceDisplayLanguage()
  const { fieldwork } = useFieldwork()
  const client = useApolloClient()
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const members = audienceState.audienceMembers
  // Mutations are serialised: HTTP ordering is not guaranteed, and two moves
  // racing would have the server apply them against unexpected intermediate
  // orders.
  const pending = useRef<Promise<unknown>>(Promise.resolve())
  // Bumped on refusal so gestures queued against the discarded order drop out.
  const queueGeneration = useRef(0)

  const rows: ReorderAudienceRowData[] = useMemo(() => {
    const labels = getSurveyQuestionLabels(members)
    return members.map((member, index) => {
      const question = getAudienceMemberQuestion(member)
      return {
        memberId: member.id,
        label: labels[index],
        text: overlayQuestionText(
          question.text,
          question.translations,
          marketLanguage
        )
      }
    })
  }, [members, marketLanguage])

  // The context is a mirror two lists refill from draftAudienceV2, and the
  // mutation's own result makes Apollo re-read it with the old order — so the
  // cache has to carry the new order too, or the move visibly springs back.
  const applyOrder = useCallback(
    (ordered: FieldworkAudience[]) => {
      client.cache.updateQuery<AudienceData>(
        { query: GET_AUDIENCES, variables: { projectId, surveyId, waveId } },
        (data) =>
          data && {
            ...data,
            draftAudience: { ...data.draftAudience, members: ordered }
          }
      )
      dispatch(setAudienceMembers(ordered))
    },
    [client, projectId, surveyId, waveId, dispatch]
  )

  const handleMove = useCallback(
    (memberId: string, toIndex: number) => {
      const member = members.find((m: FieldworkAudience) => m.id === memberId)
      if (!member || toIndex < 0 || toIndex >= members.length) {
        return
      }
      const previous = members
      const next = moveMemberInList(members, memberId, toIndex)
      // react-dnd fires on every release, so a drop that settles a row back where
      // it started reaches here. Compare the resulting order, not the array
      // identity — any member update clears the market's preset audience.
      if (next.every((m, index) => m.id === previous[index].id)) {
        return
      }

      applyOrder(next)
      const generation = queueGeneration.current
      pending.current = pending.current.then(async () => {
        if (queueGeneration.current !== generation) {
          return
        }
        const ok = await updateAudienceMemberV2Position(
          getAudienceMemberQuestionId(member),
          toIndex
        )
        if (!ok) {
          queueGeneration.current += 1
          applyOrder(previous)
        }
      })
    },
    [members, applyOrder, updateAudienceMemberV2Position]
  )

  const presetName =
    fieldwork?.presetAudience?.name &&
    fieldwork.presetAudience.name !== CUSTOM_AUDIENCE_PRESET_NAME
      ? fieldwork.presetAudience.name
      : undefined

  return (
    <ReorderAudienceDialogControl
      open={open}
      rows={rows}
      presetName={presetName}
      onClose={onClose}
      onMove={handleMove}
    />
  )
}

export default ReorderAudienceDialog
